import { COMBO_ALWAYS, COMBO_WHEN_ALL_SELECTED, COMBO_WHEN_ANY_SELECTED } from "../api/constants";
import { Combo, Meal, MealLine, MealsList, Order, PriceMealsExtra, SnackLine, SnacksList } from "../types";
import { applyDiscountToPrice, getPersonnelPrice, getPrice, getPriceCombo, getPriceComboWithDiscount, getPriceDessertWithDiscount, getPriceSideWithDiscount, getPriceWithDiscount, roundEuro } from "./pricing";

/**
 * Gets combo mode from the new database structure
 */
export function getComboMode(isStaff: boolean, combosData?: { [key: string]: Combo }): number | undefined {
    if (!combosData)
        return undefined;
    const comboType = isStaff ? 'personnelCombo' : 'studentCombo';
    return combosData[comboType]?.mode;
}

/**
 * Calculate the price for a single snack line with all applicable discounts
 */
export function calculateSnackLinePrice(
    snackLine: SnackLine,
    snacks: SnacksList,
    recipients: { [key: string]: { personnel?: boolean, student?: boolean } },
    staffDiscount: number,
    topupDiscountPercentage: number
): number {
    const { snackKey, quantity, recipient } = snackLine;
    const isStaff = recipients[recipient]?.personnel === true;
    const basePrice = snacks[snackKey]?.price ?? 0;
    const isStaffDiscountable = isStaff && staffDiscount > 0;

    const linePrice: number = isStaffDiscountable ?
        quantity * getPersonnelPrice(basePrice, staffDiscount) :
        quantity * applyDiscountToPrice(basePrice, topupDiscountPercentage);

    return Math.round(linePrice * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate the price for a single meal line with all applicable discounts
 */
export function calculateMealLinePrice(
    mealLine: MealLine,
    meals: MealsList,
    dailyMeals: MealsList,
    recipients: { [key: string]: { personnel?: boolean, student?: boolean } },
    staffDiscount: number,
    pricesExtra: PriceMealsExtra,
    sideDish: Meal,
    dessert: Meal,
    combosData?: { [key: string]: Combo },
    topupDiscountPercentage: number = 0,
    menuData?: { [key: string]: Meal },
    chargePackageExchangePrice: boolean = true
): number {
    const { key, quantity, quantitySide, quantityDessert, isPackage, packagePrice, portionSize, recipient } = mealLine;

    // Check if this specific recipient is staff
    const isStaff = !!recipients[recipient]?.personnel;

    // Merge menu meals and daily meals for price lookup
    const combinedMeals: MealsList = {
        ...(meals || {}),
        ...(dailyMeals || {}),
    } as MealsList;

    // For menu items, use menu data if available, otherwise fall back to combined meals data
    const mealData = menuData && menuData[key] ? menuData[key] : combinedMeals[key];
    const isSetMenu = mealData?.courseType === 'main';

    // Combo logic
    const comboSetting = getComboMode(isStaff, combosData);
    const isComboAlways = comboSetting === COMBO_ALWAYS;
    const isComboWhenAllSelected = comboSetting === COMBO_WHEN_ALL_SELECTED;
    const isComboWhenAnySelected = comboSetting === COMBO_WHEN_ANY_SELECTED;

    // Check if all items are selected for combo when all selected
    const allItemsSelected = quantitySide > 0 && quantityDessert > 0;
    // Check if any items are selected for combo when any selected
    const anyItemsSelected = quantitySide > 0 || quantityDessert > 0;
    const shouldUseComboPrice = isComboAlways || (isComboWhenAllSelected && allItemsSelected) || (isComboWhenAnySelected && anyItemsSelected);

    const hasStaffComboPrice = (isSetMenu && shouldUseComboPrice) && (!!mealData?.priceComboStaff || !!(combosData as any)?.prices?.priceStaff);
    const isStaffDiscountable = isStaff && staffDiscount > 0 && (isSetMenu || mealData?.isExchangable || mealData?.applyStaffDiscount) && !hasStaffComboPrice;

    let linePrice: number;

    if (isPackage) {
        // If charging is disabled, package exchanges are free
        linePrice = chargePackageExchangePrice ? packagePrice || 0 : 0;
    }
    else {
        // Calculate meal price with appropriate discounts
        let finalPrice: number | null | undefined;
        if (!isStaffDiscountable) {
            // Students or staff without staff discount: apply topup discount
            finalPrice = isSetMenu && shouldUseComboPrice ?
                getPriceComboWithDiscount(portionSize, mealData, combosData, topupDiscountPercentage, isStaff) :
                getPriceWithDiscount(portionSize, mealData?.priceSmall, mealData?.priceMedium, mealData?.price, topupDiscountPercentage);
        } else {
            // Staff with staff discount: only apply staff discount, no topup discount
            const basePrice = isSetMenu && shouldUseComboPrice ?
                getPriceCombo(portionSize, mealData, combosData, isStaff) :
                getPrice(portionSize, mealData?.priceSmall, mealData?.priceMedium, mealData?.price);
            finalPrice = basePrice != null ? getPersonnelPrice(basePrice, staffDiscount) : null;
        }

        // Handle null/undefined prices - default to 0 if price cannot be determined
        if (finalPrice == null || isNaN(finalPrice)) {
            linePrice = 0;
        } else {
            linePrice = quantity * finalPrice;

            if (isSetMenu && !shouldUseComboPrice) {
                // For set menus
                if (!!quantitySide) {
                    const sidePrice = mealData?.priceSide || sideDish?.price || getPriceSideWithDiscount(portionSize, pricesExtra, topupDiscountPercentage);
                    linePrice += quantitySide * (sidePrice ?? 0);
                }
                if (!!quantityDessert) {
                    const dessertPrice = mealData?.priceDessert || dessert?.price || getPriceDessertWithDiscount(portionSize, pricesExtra, topupDiscountPercentage);
                    linePrice += quantityDessert * (dessertPrice ?? 0);
                }
            }
        }
    }

    // linePrice is already validated to be a number (0 or calculated value)
    // Round to 2 decimal places
    return Math.round(linePrice * 100) / 100;
}

export function orderStats(uid: string, order: Order, snacks: SnacksList, meals: MealsList, dailyMeals: MealsList, recipients: { [key: string]: { personnel?: boolean, student?: boolean } }, staffDiscount: number, pricesExtra: PriceMealsExtra, sideDish: Meal, dessert: Meal, setDayPrice?: number, combosData?: { [key: string]: Combo }, topupDiscountPercentage: number = 0, menuData?: { [key: string]: Meal }, chargePackageExchangePrice: boolean = true) {

    if (!order || !snacks || !(meals || dailyMeals))
        return undefined;

    // Calculate prices for snacks using the helper function
    const snacksStats = Object.values(order.snacks ?? {}).reduce((prev, line) => {
        const linePrice = calculateSnackLinePrice(line, snacks, recipients, staffDiscount, topupDiscountPercentage);
        return {
            p: prev.p + linePrice,
            c: prev.c + 1
        }
    }, { p: 0, c: 0 });

    // Calculate prices for meals using the helper function
    const mealsStats = Object.values(order.meals ?? {}).reduce((prev, line) => {
        const linePrice = calculateMealLinePrice(
            line,
            meals,
            dailyMeals,
            recipients,
            staffDiscount,
            pricesExtra,
            sideDish,
            dessert,
            combosData,
            topupDiscountPercentage,
            menuData,
            chargePackageExchangePrice
        );
        return {
            p: prev.p + linePrice,
            c: prev.c + 1
        }
    }, { p: 0, c: 0 });

    const rawTotalPrice = setDayPrice ? setDayPrice : snacksStats.p + mealsStats.p;

    return {
        totalPrice: Math.round(rawTotalPrice * 100) / 100, // Round to 2 decimal places to avoid floating point precision issues
        itemsCount: snacksStats.c + mealsStats.c,
    };
}

/** Placed order is committed (registered) — same gate as processOrderDay change/cancel and basket new vs change. */
export function isCommittedOrder(existingOrder: Order | null | undefined): boolean {
    return !!existingOrder && (existingOrder.price ?? -1) >= 0 && (existingOrder.timestamp ?? 0) > 0;
}

/** Ordering-relevant fingerprint of one draft/placed line; ignores prices, push ids and metadata. */
function normalizeOrderLine(line: MealLine | SnackLine | null, type: 'meal' | 'snack'): string | null {
    if (!line) return null;
    const key = type === 'meal' ? (line as MealLine).key : (line as SnackLine).snackKey;
    const quantity = line.quantity ?? 0;
    if (!key || quantity <= 0) return null;
    return JSON.stringify({
        type,
        key,
        quantity,
        recipient: line.recipient ?? null,
        schoolBreak: line.schoolBreak ?? null,
        portionSize: line.portionSize ?? null,
        quantitySide: line.quantitySide ?? null,
        quantityDessert: line.quantityDessert ?? null,
        orderItemRemark: line.orderItemRemark || null,
        isPackage: !!line.isPackage,
    });
}

function normalizedOrderLines(order: Order | null): string[] {
    if (!order) return [];
    const lines = [
        ...Object.values(order.meals ?? {}).map(line => normalizeOrderLine(line, 'meal')),
        ...Object.values(order.snacks ?? {}).map(line => normalizeOrderLine(line, 'snack')),
    ].filter((l): l is string => !!l);
    return lines.sort();
}

/** Content-based comparison key: same meals/snacks/remarks → same key (unitPrice/timestamps/ids excluded). */
export function normalizeOrderContent(order: Order | null): string {
    return JSON.stringify({ lines: normalizedOrderLines(order), remarks: order?.orderRemarks || '' });
}

export function orderHasContentLines(order: Order | null): boolean {
    return normalizedOrderLines(order).length > 0;
}

/** Draft lines are only package placeholders (pre-checkout / pre-copy), not snacks or remarks. */
export function orderDraftIsPackageOnly(order: Order | null): boolean {
    if (!order || !orderHasContentLines(order)) {
        return false;
    }
    const meals = Object.values(order.meals ?? {});
    if (meals.length === 0 || !meals.every((m) => m?.isPackage)) {
        return false;
    }
    if (Object.keys(order.snacks ?? {}).length > 0) {
        return false;
    }
    if (order.orderRemarks) {
        return false;
    }
    return true;
}

/** Parent edited a `pkgCopy_*` slot on the draft (exchange/remark) while placed still has the registered meal. */
export function orderDraftHasPendingPackageSlotEdits(draft: Order | null, placed: Order | null): boolean {
    if (!draft?.meals || !placed?.meals) {
        return false;
    }
    for (const [slotKey, draftLine] of Object.entries(draft.meals)) {
        if (!slotKey.startsWith('pkgCopy_') || !draftLine?.isPackage) {
            continue;
        }
        const placedLine = placed.meals[slotKey];
        if (!placedLine?.isPackage) {
            continue;
        }
        if (normalizeOrderLine(draftLine, 'meal') !== normalizeOrderLine(placedLine, 'meal')) {
            return true;
        }
    }
    return false;
}

export type BasketCheckoutDayInput = {
    newPrice: number;
    oldPrice?: number;
};

export type BasketCheckoutTotals = {
    totalNewPrice: number;
    totalRefunds: number;
    netDue: number;
    creditsUsed: number;
    payAmount: number;
    /** Amount credited back to wallet when netDue < 0 (UI). */
    creditsRefund: number;
    balanceAfterCheckout: number;
};

/** Multi-day basket rollup after per-day newPrice / oldPrice are known. Credits are mandatory. */
export function computeBasketCheckoutTotals(days: BasketCheckoutDayInput[], balance: number): BasketCheckoutTotals {
    const totalNewPrice = roundEuro(days.reduce((sum, d) => sum + d.newPrice, 0));
    const totalRefunds = roundEuro(days.reduce((sum, d) => sum + (d.oldPrice ?? 0), 0));
    const netDue = roundEuro(totalNewPrice - totalRefunds);
    const creditsRefund = netDue < 0 ? roundEuro(-netDue) : 0;
    const creditsUsed = roundEuro(Math.min(Math.max(balance, 0), Math.max(netDue, 0)));
    const payAmount = roundEuro(Math.max(netDue - creditsUsed, 0));
    const balanceAfterCheckout = netDue < 0
        ? roundEuro(balance + creditsRefund)
        : roundEuro(Math.max(balance - creditsUsed, 0));
    return { totalNewPrice, totalRefunds, netDue, creditsUsed, payAmount, creditsRefund, balanceAfterCheckout };
}